import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';
import './StaffHRPage.css';

const StaffHRPage = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const canAccess = currentUser && currentUser.hr;

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/hr/jobs');
      setJobs(response.data.jobs || []);
    } catch (err) {
      console.error('Error fetching jobs:', err);
      setError(err.response?.data?.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await api.get('/api/hr/applications');
      setApplications(response.data.applications || []);
    } catch (err) {
      console.error('Error fetching applications:', err);
    }
  };

  useEffect(() => {
    if (canAccess) {
      fetchJobs();
      fetchApplications();
    }
  }, [canAccess]);

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job posting?')) return;
    try {
      await api.delete(`/api/hr/jobs/${jobId}`);
      setJobs((prev) => prev.filter(job => job._id !== jobId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete job');
    }
  };

  const togglePublish = async (jobId) => {
    await api.patch(`/api/hr/jobs/${jobId}/publish`);
    fetchJobs();
  };

  const toggleFeature = async (jobId) => {
    await api.patch(`/api/hr/jobs/${jobId}/feature`);
    fetchJobs();
  };

  const stats = useMemo(() => ({
    total: applications.filter(a => a.status !== 'archived').length,
    submitted: applications.filter(a => a.status === 'submitted').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length
  }), [applications]);

  if (!canAccess) {
    return (
      <div className="staff-hr-page">
        <div className="error-message">You do not have HR access.</div>
      </div>
    );
  }

  return (
    <div className="staff-hr-page">
      <div className="page-header">
        <h1>👥 HR Staff Dashboard</h1>
        <p>Manage job postings and review applicants.</p>
        <div className="page-header-actions">
          <button className="btn-primary" onClick={() => navigate('/staff/hr/jobs/new')}>
            + Create Job Posting
          </button>
          <button className="btn-secondary" onClick={() => navigate('/staff/hr/applications')}>
            Open Applications Workspace
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <section className="job-list-section">
        <h2>Posted Jobs</h2>
        <div className="jobs-grid">
          {jobs.map(job => (
            <div key={job._id} className="job-card">
              <div className="job-card-header">
                <h3>{job.title}</h3>
                <div className="job-card-actions">
                  <button onClick={() => navigate(`/staff/hr/jobs/${job._id}/edit`)} className="btn-link">Edit</button>
                  <button onClick={() => handleDelete(job._id)} className="btn-link danger">Delete</button>
                </div>
              </div>
              <p className="job-meta">{job.department || 'Department'} • {job.location || 'Location'}</p>
              <div className="job-tags">
                <span className={job.isPublished ? 'tag published' : 'tag draft'}>
                  {job.isPublished ? 'Published' : 'Draft'}
                </span>
                {job.isFeatured && <span className="tag featured">Featured</span>}
              </div>
              <div className="job-card-footer">
                <button className="btn-secondary" onClick={() => togglePublish(job._id)}>
                  {job.isPublished ? 'Unpublish' : 'Publish'}
                </button>
                <button className="btn-secondary" onClick={() => toggleFeature(job._id)}>
                  {job.isFeatured ? 'Unfeature' : 'Feature'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="application-section">
        <div className="section-header-row">
          <h2>Applications Workspace</h2>
          <button className="btn-primary" onClick={() => navigate('/staff/hr/applications')}>
            Manage Applications
          </button>
        </div>
        <div className="stats-grid">
          <div className="stat-card"><strong>{stats.total}</strong><span>Total Active</span></div>
          <div className="stat-card"><strong>{stats.submitted}</strong><span>Submitted</span></div>
          <div className="stat-card"><strong>{stats.reviewed}</strong><span>In Review</span></div>
          <div className="stat-card"><strong>{stats.approved}</strong><span>Approved</span></div>
          <div className="stat-card"><strong>{stats.rejected}</strong><span>Rejected</span></div>
        </div>
      </section>
    </div>
  );
};

export default StaffHRPage;
